import { decryptBlob } from './decrypt'
import { renderDebriefHtml } from './render-debrief-html'
import type { DebriefSharePayload } from './types'

function isDebriefSharePayload(v: unknown): v is DebriefSharePayload {
  if (typeof v !== 'object' || v === null) return false
  const r = v as Record<string, unknown>
  return r.version === 1 && typeof r.meetingTitle === 'string' && typeof r.data === 'object' && r.data !== null
}

const app = document.getElementById('app')!

function setMessage(msg: string, isError = false): void {
  app.innerHTML = `<div class="state-msg ${isError ? 'err' : ''}">${msg}</div>`
}

function extractTokenFromPath(): string | null {
  const m = window.location.pathname.match(/^\/([A-Za-z0-9_-]{16,128})\/?$/)
  return m ? (m[1] ?? null) : null
}

function extractKeyFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, '').trim()
  if (!hash) return null
  if (!/^[A-Za-z0-9_-]{43}$/.test(hash)) return null
  return hash
}

async function main(): Promise<void> {
  const token = extractTokenFromPath()
  if (!token) {
    setMessage('Invalid share link.', true)
    return
  }
  const key = extractKeyFromHash()
  if (!key) {
    setMessage(
      'This link is missing its decryption key (the part after #). Make sure you copied the entire URL.',
      true,
    )
    return
  }

  const apiBase = import.meta.env.VITE_API_BASE || ''
  const blobUrl = `${apiBase}/share-blob/${encodeURIComponent(token)}`
  let response: Response
  try {
    response = await fetch(blobUrl, { credentials: 'include' })
  } catch (err) {
    setMessage('Network error while fetching the debrief.', true)
    console.error('[viewer] fetch failed', err)
    return
  }

  if (response.status === 401) {
    setMessage(
      'Please sign in with your Opera account to view this debrief, then reload the page.',
      true,
    )
    return
  }
  if (response.status === 403) {
    setMessage('Your account is not authorized to view this debrief.', true)
    return
  }
  if (response.status === 404) {
    setMessage('This debrief no longer exists. It may have been revoked or expired.', true)
    return
  }
  if (!response.ok) {
    setMessage(`Failed to load debrief (HTTP ${response.status}).`, true)
    return
  }

  let blob: ArrayBuffer
  try {
    blob = await response.arrayBuffer()
  } catch (err) {
    setMessage('Failed to read share data. The link may be invalid or expired.', true)
    console.error('[viewer] arrayBuffer failed', err)
    return
  }

  let raw: unknown
  try {
    raw = await decryptBlob(blob, key)
  } catch (err) {
    setMessage(
      'Failed to decrypt the debrief. The link key may be wrong or corrupted.',
      true,
    )
    console.error('[viewer] decrypt failed', err)
    return
  }

  if (!isDebriefSharePayload(raw)) {
    setMessage(
      'This debrief is in an unrecognised format. The share may have been created by a newer version of Meeting Cues.',
      true,
    )
    console.error('[viewer] unexpected payload shape', raw)
    return
  }
  const payload = raw

  document.title = `${payload.meetingTitle} — Debrief`
  app.innerHTML = renderDebriefHtml(payload)
}

main().catch((err) => {
  console.error('[viewer] unhandled', err)
  setMessage('An unexpected error occurred while loading the debrief.', true)
})
